import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  PerspectiveCamera,
  Points,
  PointsMaterial,
  Sprite,
  SpriteMaterial,
  TorusGeometry,
  Vector3,
  type Scene,
} from "three";
import type { HoleLayout } from "@shared/physics";

// Vibeverse portal (Vibe Jam 2026 cross-game traffic).
// Adapted from levelsio's sample gist:
//   https://gist.github.com/levelsio/ffdbfe356b421b97a31664ded4bc961d
// Scaled down to fit our 30-40-unit-wide arena (gist used 15-unit radii
// in a much larger world). Our ball physics is strictly 2D/xz so we
// trigger on a horizontal distance check against the ball's current
// position, not the 3D box intersection from the original.

const TRIGGER_RADIUS = 2.8;
const EXIT_TARGET = "https://vibej.am/portal/2026";

export interface PortalHandle {
  group: Group;
  /** Call each frame; returns a destination URL when the ball enters a
   *  portal, or null otherwise. */
  checkBall(ballX: number, ballZ: number): string | null;
  /** Advance the shader / particle animation. Pass the live camera so
   *  label sprites billboard toward the viewer each frame. */
  update(t: number, camera?: PerspectiveCamera): void;
  dispose(): void;
}

export function mountPortals(
  scene: Scene,
  hole: HoleLayout,
): PortalHandle {
  const root = new Group();
  scene.add(root);

  // Exit portal — placed far from the tee on a perpendicular bearing
  // from the tee→target axis, so it reads as a second "goal" in a
  // different direction (not a distraction on the main line).
  const exitAnchor = placeExitPortal(hole);
  const exitPortal = buildPortal({
    position: exitAnchor,
    color: "#5cff9a",
    label: "VIBE JAM PORTAL",
  });
  root.add(exitPortal.group);

  // Start portal — only shown when the player arrived from another jam
  // game via `?portal`. Sits near the target so they can aim back out.
  const params = new URLSearchParams(window.location.search);
  const cameFromPortal = params.get("portal") === "true" || params.get("portal") === "1";
  const referrer = params.get("ref");
  let startPortal: ReturnType<typeof buildPortal> | null = null;
  if (cameFromPortal) {
    startPortal = buildPortal({
      position: placeStartPortal(hole),
      color: "#ff6a6a",
      label: referrer ? `← ${truncate(referrer, 22)}` : "← BACK",
    });
    root.add(startPortal.group);
  }

  function checkBall(ballX: number, ballZ: number): string | null {
    const dxExit = ballX - exitAnchor.x;
    const dzExit = ballZ - exitAnchor.z;
    if (Math.hypot(dxExit, dzExit) < TRIGGER_RADIUS) {
      return buildExitUrl(params);
    }
    if (startPortal && referrer) {
      const dxStart = ballX - startPortal.position.x;
      const dzStart = ballZ - startPortal.position.z;
      if (Math.hypot(dxStart, dzStart) < TRIGGER_RADIUS) {
        return buildReturnUrl(referrer, params);
      }
    }
    return null;
  }

  function update(t: number, camera?: PerspectiveCamera): void {
    exitPortal.update(t, camera);
    startPortal?.update(t, camera);
  }

  return {
    group: root,
    checkBall,
    update,
    dispose() {
      scene.remove(root);
      exitPortal.dispose();
      startPortal?.dispose();
    },
  };
}

function placeExitPortal(hole: HoleLayout): Vector3 {
  // Portal reads as a *second* goal in a different direction. Sit it
  // roughly as far from the tee as the target, but on a perpendicular
  // bearing across the tee→target axis, so it's a genuine choice — you
  // aim at the target OR you aim at the portal, not both on one line.
  const dx = hole.target.cx - hole.tee.x;
  const dz = hole.target.cz - hole.tee.z;
  const len = Math.hypot(dx, dz) || 1;
  const ux = dx / len;
  const uz = dz / len;
  // Perpendicular in the xz plane (90° CCW from tee→target direction).
  const px = -uz;
  const pz = ux;
  // Travel 80% of the way toward the target, then step out
  // perpendicular to reach a distinct quadrant.
  const travel = len * 0.75;
  const perpOffset = Math.min(len * 0.55, 14);
  const px_ = hole.tee.x + ux * travel + px * perpOffset;
  const pz_ = hole.tee.z + uz * travel + pz * perpOffset;
  // Clamp to bounds with a healthy margin.
  const cx = Math.max(hole.bounds.minX + 4, Math.min(hole.bounds.maxX - 4, px_));
  const cz = Math.max(hole.bounds.minZ + 4, Math.min(hole.bounds.maxZ - 4, pz_));
  return new Vector3(cx, 1.6, cz);
}

function placeStartPortal(hole: HoleLayout): Vector3 {
  // Mirror of exit but on the *other* perpendicular side, so inbound
  // jam players see their return portal equally distinct.
  const dx = hole.target.cx - hole.tee.x;
  const dz = hole.target.cz - hole.tee.z;
  const len = Math.hypot(dx, dz) || 1;
  const ux = dx / len;
  const uz = dz / len;
  const px = uz; // opposite perpendicular from the exit portal
  const pz = -ux;
  const travel = len * 0.75;
  const perpOffset = Math.min(len * 0.55, 14);
  const px_ = hole.tee.x + ux * travel + px * perpOffset;
  const pz_ = hole.tee.z + uz * travel + pz * perpOffset;
  const cx = Math.max(hole.bounds.minX + 4, Math.min(hole.bounds.maxX - 4, px_));
  const cz = Math.max(hole.bounds.minZ + 4, Math.min(hole.bounds.maxZ - 4, pz_));
  return new Vector3(cx, 1.6, cz);
}

interface PortalParts {
  group: Group;
  position: Vector3;
  update(t: number, camera?: PerspectiveCamera): void;
  dispose(): void;
}

function buildPortal(opts: {
  position: Vector3;
  color: string;
  label: string;
}): PortalParts {
  const group = new Group();
  group.position.copy(opts.position);
  // Tilt ~20° so the portal face angles slightly toward the camera.
  group.rotation.x = 0.35;

  const color = new Color(opts.color);

  const torusGeom = new TorusGeometry(1.8, 0.22, 16, 72);
  const torusMat = new MeshPhongMaterial({
    color,
    emissive: color,
    transparent: true,
    opacity: 0.85,
  });
  const torus = new Mesh(torusGeom, torusMat);
  group.add(torus);

  const innerGeom = new CircleGeometry(1.6, 48);
  const innerMat = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.45,
    side: DoubleSide,
  });
  const inner = new Mesh(innerGeom, innerMat);
  group.add(inner);

  // Label floats above the portal ring. Sprite auto-billboards so the
  // text stays legible from any angle, and sits high enough above the
  // torus not to clip into it.
  const label = buildLabel(opts.label, color);
  label.position.y = 3.4;
  group.add(label);

  // Particle swirl around the ring. Fewer particles than the gist's 1000
  // — we're a tiny mobile-friendly scene.
  const particleCount = 220;
  const positions = new Float32Array(particleCount * 3);
  const seeds = new Float32Array(particleCount);
  for (let i = 0; i < particleCount; i++) {
    const angle = Math.random() * Math.PI * 2;
    const r = 1.8 + (Math.random() - 0.5) * 0.55;
    positions[i * 3] = Math.cos(angle) * r;
    positions[i * 3 + 1] = Math.sin(angle) * r;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
    seeds[i] = Math.random() * Math.PI * 2;
  }
  const particleGeom = new BufferGeometry();
  particleGeom.setAttribute("position", new BufferAttribute(positions, 3));
  const particleMat = new PointsMaterial({
    size: 0.14,
    color,
    transparent: true,
    opacity: 0.75,
    sizeAttenuation: true,
    depthWrite: false,
  });
  const particles = new Points(particleGeom, particleMat);
  group.add(particles);

  function update(t: number, _camera?: PerspectiveCamera): void {
    // Gentle vertical bob so the portal feels alive.
    torus.rotation.z = t * 0.7;
    inner.material.opacity = 0.35 + Math.sin(t * 2.2) * 0.15;
    const posAttr = particleGeom.getAttribute("position") as BufferAttribute;
    const arr = posAttr.array as Float32Array;
    for (let i = 0; i < particleCount; i++) {
      arr[i * 3 + 1] += Math.sin(t * 2.5 + seeds[i]!) * 0.003;
    }
    posAttr.needsUpdate = true;
    // Sprite-based labels billboard automatically — no lookAt needed.
  }

  function dispose(): void {
    torusGeom.dispose();
    torusMat.dispose();
    innerGeom.dispose();
    innerMat.dispose();
    particleGeom.dispose();
    particleMat.dispose();
    const labelMat = label.material as SpriteMaterial;
    labelMat.map?.dispose();
    labelMat.dispose();
  }

  return { group, position: group.position.clone(), update, dispose };
}

function buildLabel(text: string, color: Color): Sprite {
  // Higher-res canvas so the text stays crisp when the sprite billboards
  // close to the camera. Sprites always face the viewer automatically,
  // so no per-frame lookAt bookkeeping is needed.
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 160;
  const ctx = canvas.getContext("2d")!;
  const hex = `rgba(${Math.round(color.r * 255)}, ${Math.round(
    color.g * 255,
  )}, ${Math.round(color.b * 255)}, 1)`;
  // Soft glow behind the text so it pops on the starfield.
  ctx.shadowColor = hex;
  ctx.shadowBlur = 28;
  ctx.fillStyle = hex;
  ctx.font = "bold 72px 'SF Pro Display', Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);
  const texture = new CanvasTexture(canvas);
  texture.needsUpdate = true;
  const mat = new SpriteMaterial({
    map: texture,
    transparent: true,
    depthWrite: false,
    // Draw on top of the asteroid wall / planets so the portal is
    // always readable at a glance, from any camera angle.
    depthTest: false,
  });
  const sprite = new Sprite(mat);
  // Matches the 6.4:1 canvas aspect so the text isn't stretched.
  sprite.scale.set(7.2, 1.125, 1);
  sprite.renderOrder = 10;
  return sprite;
}

function buildExitUrl(params: URLSearchParams): string {
  const next = new URLSearchParams();
  next.append("portal", "true");
  // The jam expects these so the next game can greet the player.
  next.append("ref", "cosmicpar.com");
  next.append("color", "white");
  next.append("speed", "0");
  // Pass through any existing params the player was carrying, so their
  // chain can reconstruct on the far side.
  for (const [k, v] of params) {
    if (!next.has(k)) next.append(k, v);
  }
  return `${EXIT_TARGET}?${next.toString()}`;
}

function buildReturnUrl(ref: string, params: URLSearchParams): string {
  let base = ref;
  if (!base.startsWith("http://") && !base.startsWith("https://")) {
    base = `https://${base}`;
  }
  const next = new URLSearchParams();
  for (const [k, v] of params) {
    if (k !== "ref") next.append(k, v);
  }
  const qs = next.toString();
  return qs ? `${base}?${qs}` : base;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
